import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Building2, ShieldCheck, GraduationCap } from 'lucide-react';
import Seo from '../components/Seo';

const LOGO_URL = "/inasw-logo.jpg";
const LOGIN_IMAGE = "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/ney4lolg_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2012_40_51%20PM.png";
const REGISTER_IMAGE = "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/0yai65vd_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2012_40_48%20PM.png";

// "How did you hear about us?" options + smart follow-up prompts (marketing attribution)
const REFERRAL_SOURCES = [
  { value: 'google', label: 'Google or another search engine' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'friend', label: 'A friend or colleague', followUp: 'Who can we thank for referring you? (optional)', followUpRequired: false },
  { value: 'school', label: 'A social work school or university', followUp: 'Which school or university?', followUpRequired: true },
  { value: 'association', label: 'A professional association (NASW, etc.)', followUp: 'Which association?', followUpRequired: true },
  { value: 'conference', label: 'A conference or event', followUp: 'Which conference or event?', followUpRequired: true },
  { value: 'employer', label: 'My employer or organization', followUp: 'Which organization?', followUpRequired: true },
  { value: 'newsletter', label: 'An email or newsletter' },
  { value: 'podcast', label: 'A podcast', followUp: 'Which podcast?', followUpRequired: true },
  { value: 'flyer', label: 'A flyer, postcard, or QR code', followUp: 'Where did you see it?', followUpRequired: true },
  { value: 'other', label: 'Something else', followUp: 'Please tell us how', followUpRequired: true },
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Where to send the user after login: the page they originally requested, if any.
  const fromDest = location.state?.from || new URLSearchParams(location.search).get('redirect') || null;
  const goAfterLogin = (u) => {
    if (fromDest && fromDest !== '/login') { navigate(fromDest, { replace: true }); return; }
    if (u.user_type === 'employer') navigate('/employer/subscription');
    else if (u.user_type === 'admin') navigate('/admin');
    else if (u.user_type === 'supervisor') navigate('/supervisor/dashboard');
    else navigate('/dashboard');
  };

  // Redirect already logged-in users to their requested page or dashboard
  useEffect(() => {
    if (isAuthenticated && user) goAfterLogin(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      goAfterLogin(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16 pb-12 px-4">
      <Seo
        exactTitle="Log In to Your Account | I Need A Social Worker"
        description="Sign in to your I Need A Social Worker account to manage job applications, saved searches, license reminders, supervision and your professional profile."
        path="/login"
      />
      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Image Section - Hidden on mobile */}
        <div className="hidden lg:block">
          <img 
            src={LOGIN_IMAGE} 
            alt="Social work professional workspace" 
            className="w-full h-auto rounded-2xl shadow-lg"
          />
        </div>
        
        {/* Form Section */}
        <div className="w-full max-w-md mx-auto lg:max-w-none">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-3 mb-6">
                <img src={LOGO_URL} alt="Logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-secondary/50 shadow-sm" />
                <span className="font-serif text-xl font-semibold text-primary">
                  I Need A Social Worker
                </span>
              </Link>
              <h1 className="font-serif text-2xl font-bold text-gray-900">
                Welcome Back
              </h1>
              <p className="text-gray-600 mt-1">Sign in to your account</p>
            </div>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
            className="w-full h-11 mb-6"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or sign in with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-11"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary hover:bg-primary-hover text-white"
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Create one
            </Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated, user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState(searchParams.get('type') || 'job_seeker');
  const redirectTo = searchParams.get('redirect');
  const [marketingSource, setMarketingSource] = useState('');
  const [marketingDetail, setMarketingDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const didRegister = useRef(false);

  const selectedSource = REFERRAL_SOURCES.find((s) => s.value === marketingSource);

  // Redirect already authenticated users
  useEffect(() => {
    if (didRegister.current) return;
    if (isAuthenticated && user) {
      if (redirectTo) {
        navigate(redirectTo);
      } else if (user.user_type === 'employer') {
        navigate('/employer/post-job?welcome=true');
      } else if (user.user_type === 'admin') {
        navigate('/admin');
      } else if (user.user_type === 'supervisor') {
        navigate('/become-a-supervisor');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!marketingSource) {
      toast.error('Please tell us how you heard about us');
      return;
    }
    if (selectedSource?.followUpRequired && !marketingDetail.trim()) {
      toast.error(selectedSource.followUp);
      return;
    }

    setLoading(true);
    try {
      const actualType = userType === 'student' ? 'job_seeker' : userType;
      const obRole = userType === 'student' ? 'student' : userType;
      didRegister.current = true;
      const user = await register(email, password, name, actualType, obRole, {
        marketing_source: marketingSource,
        marketing_source_detail: marketingDetail.trim() || undefined,
        featured_referral_code: searchParams.get('ref') || undefined,
      });
      toast.success('Account created successfully!');
      navigate(redirectTo || '/welcome');
    } catch (error) {
      didRegister.current = false;
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16 pb-12 px-4">
      <Seo
        exactTitle="Create Your Free Account | I Need A Social Worker"
        description="Join I Need A Social Worker free — social workers, employers, clinical supervisors and students. Find jobs, post positions, track internship hours and manage license renewals."
        path="/register"
      />
      <div className="w-full max-w-5xl grid lg:grid-cols-3 gap-6 items-start">
        {/* Image Section - Hidden on mobile */}
        <div className="hidden lg:flex justify-center items-start">
          <img 
            src={REGISTER_IMAGE} 
            alt="INeedASocialWorker.com on mobile phone" 
            className="w-56 h-auto rounded-2xl shadow-lg"
          />
        </div>
        
        {/* Form Section */}
        <div className="lg:col-span-2 w-full">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Logo */}
            <div className="text-center mb-6">
              <Link to="/" className="inline-flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-secondary/50 shadow-sm" />
                <span className="font-serif text-xl font-semibold text-primary">
                  I Need A Social Worker
                </span>
              </Link>
              <h1 className="font-serif text-2xl font-bold text-gray-900">
                Create Account
              </h1>
              <p className="text-gray-600 mt-1">Join our community</p>
            </div>

            {/* Quick Links Row */}
            <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Quick Links</h4>
                <div className="space-y-1">
                  <Link to="/jobs" className="block text-sm text-primary hover:underline">Browse All Jobs</Link>
                  <Link to="/jobs?license=LCSW" className="block text-sm text-primary hover:underline">LCSW Jobs</Link>
                  <Link to="/jobs?license=LMSW" className="block text-sm text-primary hover:underline">LMSW Jobs</Link>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">For Employers</h4>
                <div className="space-y-1">
                  <Link to="/register?type=employer" className="block text-sm text-primary hover:underline">Post a Job</Link>
                  <Link to="/login" className="block text-sm text-primary hover:underline">Employer Login</Link>
                </div>
              </div>
            </div>

            {/* User Type Selection */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 text-center mb-3">I am a...</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUserType('job_seeker')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === 'job_seeker'
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="job-seeker-type-btn"
              >
                <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                  userType === 'job_seeker' ? 'bg-primary/10' : 'bg-gray-100'
                }`}>
                  <User className={`w-5 h-5 ${userType === 'job_seeker' ? 'text-primary' : 'text-gray-400'}`} />
                </div>
                <p className={`text-sm font-semibold ${userType === 'job_seeker' ? 'text-primary' : 'text-gray-700'}`}>
                  Job Seeker
                </p>
                <p className="text-xs text-gray-500">Looking for work</p>
              </button>
              <button
                type="button"
                onClick={() => setUserType('employer')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === 'employer'
                    ? 'border-secondary bg-secondary/5 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="employer-type-btn"
              >
                <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                  userType === 'employer' ? 'bg-secondary/10' : 'bg-gray-100'
                }`}>
                  <Building2 className={`w-5 h-5 ${userType === 'employer' ? 'text-secondary' : 'text-gray-400'}`} />
                </div>
                <p className={`text-sm font-semibold ${userType === 'employer' ? 'text-secondary' : 'text-gray-700'}`}>
                  Employer
                </p>
                <p className="text-xs text-gray-500">Hiring talent</p>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setUserType('supervisor')}
              className={`mt-3 w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                userType === 'supervisor'
                  ? 'border-secondary bg-secondary/5 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="supervisor-type-btn"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                userType === 'supervisor' ? 'bg-secondary/10' : 'bg-gray-100'
              }`}>
                <ShieldCheck className={`w-5 h-5 ${userType === 'supervisor' ? 'text-secondary' : 'text-gray-400'}`} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${userType === 'supervisor' ? 'text-secondary' : 'text-gray-700'}`}>
                  Clinical Supervisor
                </p>
                <p className="text-xs text-gray-500">Offer LCSW supervision</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setUserType('student')}
              className={`mt-3 w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                userType === 'student'
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="student-type-btn"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                userType === 'student' ? 'bg-primary/10' : 'bg-gray-100'
              }`}>
                <GraduationCap className={`w-5 h-5 ${userType === 'student' ? 'text-primary' : 'text-gray-400'}`} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${userType === 'student' ? 'text-primary' : 'text-gray-700'}`}>
                  Social Work Student / Intern
                </p>
                <p className="text-xs text-gray-500">Find internships &amp; track hours</p>
              </div>
            </button>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full h-11 mb-6"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or register with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Full Name
              </Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-11"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="pl-10 h-11"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* How did you hear about us? — required (marketing attribution) */}
            <div>
              <Label htmlFor="marketing-source" className="text-sm font-medium text-gray-700">
                How did you hear about us? <span className="text-secondary">*</span>
              </Label>
              <select
                id="marketing-source"
                value={marketingSource}
                onChange={(e) => { setMarketingSource(e.target.value); setMarketingDetail(''); }}
                required
                data-testid="marketing-source-select"
                className="mt-1 w-full h-11 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              >
                <option value="" disabled>Select an option…</option>
                {REFERRAL_SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {selectedSource?.followUp && (
              <div data-testid="marketing-detail-wrap">
                <Label htmlFor="marketing-detail" className="text-sm font-medium text-gray-700">
                  {selectedSource.followUp}{selectedSource.followUpRequired && <span className="text-secondary"> *</span>}
                </Label>
                <Input
                  id="marketing-detail"
                  type="text"
                  value={marketingDetail}
                  onChange={(e) => setMarketingDetail(e.target.value)}
                  placeholder="Type your answer"
                  className="h-11 mt-1"
                  required={selectedSource.followUpRequired}
                  data-testid="marketing-detail-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary hover:bg-primary-hover text-white"
              data-testid="register-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { googleLogin } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

      if (sessionId) {
        try {
          const user = await googleLogin(sessionId);
          toast.success('Welcome!');
          // Clear the hash
          window.history.replaceState(null, '', window.location.pathname);
          
          if (user.user_type === 'employer') {
            // Redirect employers to membership/subscription page first
            navigate('/employer/subscription', { replace: true, state: { user } });
          } else if (user.user_type === 'admin') {
            navigate('/admin', { replace: true, state: { user } });
          } else {
            navigate('/dashboard', { replace: true, state: { user } });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          toast.error('Authentication failed');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export { Login, Register, AuthCallback };
